/**
 * Welcome Offer — a one-time card shown only to eligible new customers.
 *
 * Clicking "Claim" hands the customer to `/offer-checkout/welcome` so they
 * can pick which game profile receives the Apex and accept the legal
 * acknowledgements before Stripe hand-off. This component never posts to
 * the purchase endpoint directly.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { useStoreStatus } from "../lib/storeStatus";
import { Sparkles, Gift } from "lucide-react";
import { toast } from "sonner";

export default function WelcomeOffer() {
  const [offer, setOffer] = useState(null);
  const [liveMode, setLiveMode] = useState(true);
  const { user } = useAuth();
  const { format, code, isBase } = useCurrency();
  const { salesPaused } = useStoreStatus();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/special-offers")
      .then(r => {
        const w = (r.data.offers || []).find(o => o.id === "welcome" && o.eligible);
        setOffer(w || null);
        setLiveMode(!!r.data.live_mode);
      })
      .catch(() => setOffer(null));
  }, [user?.id]);

  const claim = () => {
    if (!user) {
      toast.error("Please sign in to claim the Welcome Offer");
      nav(`/auth?mode=login&next=${encodeURIComponent("/offer-checkout/welcome")}`);
      return;
    }
    nav("/offer-checkout/welcome");
  };

  if (!offer) return null;

  const savingPct = Math.round((offer.saving_gbp / offer.normal_equivalent_price_gbp) * 100);

  return (
    <section
      id="welcome-offer"
      data-testid="welcome-offer-section"
      className="px-4 sm:px-8 max-w-7xl mx-auto mb-10 scroll-mt-28"
    >
      <div className="relative overflow-hidden rounded-2xl border-2 border-[var(--av-purple)] bg-gradient-to-br from-[#150726] via-[#0b0c10] to-[#1a0d24] shadow-[0_0_45px_-12px_rgba(148,0,211,0.55)]">
        <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[var(--av-purple)]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[var(--av-gold)]/20 blur-3xl" />
        <div className="absolute top-3 -right-8 rotate-45 w-40 text-center py-1 text-[10px] font-serif font-bold tracking-wider uppercase shadow-lg bg-gradient-to-r from-[#9400D3] to-[#5b1c8a] text-white z-10">
          NEW CUSTOMER
        </div>

        <div className="relative p-6 sm:p-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Gift className="w-4 h-4 text-[var(--av-purple)]" />
              <span className="mono-caption purple-text">Exclusive · One-Time Offer</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-extrabold mb-1">
              <span className="gold-text">Welcome Offer</span>
            </h2>
            <p className="text-lg font-semibold text-[var(--av-text)] mb-1">
              3 × 999 Apex Coin Packs
            </p>
            <p className="text-sm text-[var(--av-text-dim)] mb-4">
              {offer.total_apex.toLocaleString()} total Apex · one claim per eligible customer
            </p>
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <span data-testid="welcome-price"
                    className="font-serif text-4xl sm:text-5xl font-extrabold gold-text tabular-nums">
                {format(offer.special_price_gbp)}
              </span>
              <span className="text-lg text-[var(--av-text-muted)] line-through decoration-[var(--av-purple)]/70">
                {format(offer.normal_equivalent_price_gbp)}
              </span>
              <span data-testid="welcome-saving" className="pill pill-purple text-[11px]">
                Save {format(offer.saving_gbp)} · {savingPct}% off
              </span>
            </div>
            {!isBase && (
              <div className="text-[10px] text-[var(--av-text-muted)] mb-3" data-testid="welcome-gbp-note">
                ≈ approximate in {code} · charged £{offer.special_price_gbp.toFixed(2)} GBP
              </div>
            )}
            <div className="text-xs text-[var(--av-text)] max-w-md">
              <Sparkles className="w-3.5 h-3.5 text-[var(--av-purple)] inline align-[-2px] mr-1"/>
              Earn Apex Rewards at your normal loyalty rate on this order. Existing
              Apex Rewards <span className="purple-text font-semibold">cannot</span> be
              redeemed against special offers.
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:min-w-[240px]">
            {!liveMode && (
              <span className="pill pill-purple text-[10px] flex items-center gap-1 self-start lg:self-end">
                TEST MODE — simulated
              </span>
            )}
            <button
              data-testid="welcome-buy-btn"
              onClick={claim}
              disabled={salesPaused}
              className="btn-gold w-full shimmer disabled:opacity-40 disabled:cursor-not-allowed disabled:animate-none"
            >
              {salesPaused
                ? "Sales Paused"
                : (!user ? "Sign in to claim" : `Claim for ${format(offer.special_price_gbp)}`)}
            </button>
            <div className="text-[10px] text-center text-[var(--av-text-muted)]">
              Limit: 1 per eligible customer · never resets
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
